'use client';

import { useEffect, useRef, useState } from 'react';
import { Brain, LogIn, LogOut, Menu, Moon, Sun, User, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { TRADING_ARENA_DEPRECATED } from '../config/flags';
import { useTheme } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';

import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { FeedbackModal } from './FeedbackModal';

export function Navigation() {
  const pathname = usePathname();
  const { user, hydrated } = useUser();
  const { resolvedTheme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const ownedAgentId =
    typeof document !== 'undefined' ? document.documentElement.dataset.ownedAgent : undefined;

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/';
    return pathname === path;
  };

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [pathname]);

  const navLinkClass = (path: string) =>
    `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 ${
      isActive(path)
        ? 'border-[#1f2933] text-[#1f2933] dark:border-[#e5e5e5] dark:text-[#e5e5e5]'
        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
    }`;

  const mobileNavLinkClass = (path: string) =>
    `block px-4 py-3 text-sm font-medium border-l-4 ${
      isActive(path)
        ? 'border-[#1f2933] text-[#1f2933] bg-gray-50 dark:border-[#e5e5e5] dark:text-[#e5e5e5] dark:bg-[#222222]'
        : 'border-transparent text-gray-600 hover:text-[#1f2933] hover:bg-gray-50 hover:border-gray-300 dark:text-gray-400 dark:hover:text-[#e5e5e5] dark:hover:bg-[#222222] dark:hover:border-gray-600'
    }`;

  return (
    <>
      <nav className="border-b border-gray-200 bg-white dark:bg-[#1a1a1a] dark:border-[#2a2a2a] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left: Logo + desktop nav links */}
            <div className="flex items-center">
              <Link href="/" className="flex-shrink-0 flex items-center">
                <Brain className="h-8 w-8 text-[#1f2933] dark:text-[#e5e5e5]" />
                <span className="ml-2 text-xl font-bold text-[#1f2933] dark:text-[#e5e5e5] tracking-tight hidden sm:block">
                  Trading Arena
                </span>
                <span className="ml-2 text-xl font-bold text-[#1f2933] dark:text-[#e5e5e5] tracking-tight sm:hidden">
                  Arena
                </span>
              </Link>
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                <Link href="/" className={navLinkClass('/')}>
                  Leaderboard
                </Link>
                {user && (
                  <Link href="/app/dashboard" className={navLinkClass('/app/dashboard')}>
                    Dashboard
                  </Link>
                )}
                <Link href="/app/faq" className={navLinkClass('/app/faq')}>
                  FAQ
                </Link>
                {user && (
                  <button
                    onClick={() => setShowFeedback(true)}
                    className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium h-16 text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200 cursor-pointer"
                  >
                    Feedback
                  </button>
                )}
              </div>
            </div>

            {/* Right: desktop actions */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="p-2 rounded-lg text-gray-500 hover:text-[#1f2933] hover:bg-gray-100 dark:text-gray-400 dark:hover:text-[#e5e5e5] dark:hover:bg-[#2a2a2a] transition-colors cursor-pointer"
                aria-label={
                  resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                }
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              {!TRADING_ARENA_DEPRECATED && (
                <Link href="/app/create">
                  <Button variant={pathname === '/app/create' ? 'secondary' : 'primary'} size="sm">
                    New Agent
                  </Button>
                </Link>
              )}
              {!hydrated ? null : user ? (
                <div ref={userMenuRef} className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1f2933] dark:bg-[#e5e5e5] flex items-center justify-center">
                      <span className="text-white dark:text-[#111111] text-sm font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-[#1f2933] dark:text-[#e5e5e5]">
                      {user.name}
                    </span>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    >
                      <path
                        d="M2 3.5L5 6.5L8 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        className="text-gray-500 dark:text-gray-400"
                      />
                    </svg>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-100 dark:border-[#2a2a2a]">
                        <p className="text-sm font-medium text-[#1f2933] dark:text-[#e5e5e5]">
                          {user.name}
                        </p>
                      </div>
                      <Link
                        href="/app/dashboard"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#222222]"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          setShowLogoutConfirm(true);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <a href="/oauth/login">
                  <Button variant="secondary" size="sm">
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign in with Coinbase
                  </Button>
                </a>
              )}
            </div>

            {/* Mobile: hamburger button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="p-2 rounded-lg text-gray-500 hover:text-[#1f2933] hover:bg-gray-100 dark:text-gray-400 dark:hover:text-[#e5e5e5] dark:hover:bg-[#2a2a2a] transition-colors cursor-pointer"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#1a1a1a]">
            <div className="py-2">
              <Link href="/" className={mobileNavLinkClass('/')}>
                Leaderboard
              </Link>
              {user && (
                <Link href="/app/dashboard" className={mobileNavLinkClass('/app/dashboard')}>
                  Dashboard
                </Link>
              )}
              {!TRADING_ARENA_DEPRECATED && (
                <Link href="/app/create" className={mobileNavLinkClass('/app/create')}>
                  New Agent
                </Link>
              )}
              <Link href="/app/faq" className={mobileNavLinkClass('/app/faq')}>
                FAQ
              </Link>
              <button
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium border-l-4 border-transparent text-gray-600 hover:text-[#1f2933] hover:bg-gray-50 hover:border-gray-300 dark:text-gray-400 dark:hover:text-[#e5e5e5] dark:hover:bg-[#222222] cursor-pointer"
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )}
                {resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              {user && (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setShowFeedback(true);
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm font-medium border-l-4 border-transparent text-gray-600 hover:text-[#1f2933] hover:bg-gray-50 hover:border-gray-300 dark:text-gray-400 dark:hover:text-[#e5e5e5] dark:hover:bg-[#222222] cursor-pointer"
                >
                  Feedback
                </button>
              )}
            </div>
            <div className="border-t border-gray-200 dark:border-[#2a2a2a] py-3 px-4">
              {user ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1f2933] dark:bg-[#e5e5e5] flex items-center justify-center">
                      <span className="text-white dark:text-[#111111] text-sm font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1f2933] dark:text-[#e5e5e5]">
                        {user.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      setShowLogoutConfirm(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <a href="/oauth/login" className="block">
                  <Button variant="primary" className="w-full">
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign in with Coinbase
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}
      </nav>

      <Modal open={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} title="Sign out?">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Signing out will stop all of your running agents. Any open positions will remain in your
          portfolio.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => window.location.assign('/oauth/logout')}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </Modal>

      <FeedbackModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        page={pathname}
        agentId={ownedAgentId || undefined}
      />
    </>
  );
}
